import { useCallback, useRef, useState } from "react";
import { cn } from "@/shared/lib/utils";

const ENTRIES = [
	{ id: 1, matter: "Smith v. Jones", time: "9:00 AM", hours: "1.5h" },
	{ id: 2, matter: "Acme Corp — Contract Review", time: "10:30 AM", hours: "0.8h" },
	{ id: 3, matter: "Davis Estate Planning", time: "11:15 AM", hours: "2.0h" },
	{ id: 4, matter: "Wilson IP Filing", time: "1:00 PM", hours: "0.5h" },
	{ id: 5, matter: "Parker — Deposition Prep", time: "2:30 PM", hours: "1.2h" },
	{ id: 6, matter: "Nguyen Employment Dispute", time: "3:45 PM", hours: "0.7h" },
];

type HintType = "click" | "ctrl" | "shift" | "ctrl-shift" | null;

const HINTS: Record<string, string> = {
	click: "Click selects one entry",
	ctrl: "Ctrl+Click toggles without clearing others",
	shift: "Shift+Click selects a range",
	"ctrl-shift": "Ctrl+Shift+Click adds a range to your selection",
};

export function MultiSelectDemo() {
	const [selected, setSelected] = useState<Set<number>>(new Set());
	const [hint, setHint] = useState<HintType>(null);
	const lastClicked = useRef<number | null>(null);

	const showHint = useCallback((type: HintType) => {
		setHint(type);
		setTimeout(() => setHint((prev) => (prev === type ? null : prev)), 2000);
	}, []);

	const handleClick = useCallback((id: number, e: React.MouseEvent) => {
		const hasCtrl = e.ctrlKey || e.metaKey;
		const hasShift = e.shiftKey;

		if (hasShift && lastClicked.current !== null) {
			const ids = ENTRIES.map((en) => en.id);
			const from = ids.indexOf(lastClicked.current);
			const to = ids.indexOf(id);
			const start = Math.min(from, to);
			const end = Math.max(from, to);
			const range = ids.slice(start, end + 1);

			if (hasCtrl) {
				// Ctrl+Shift: add range
				setSelected((prev) => {
					const next = new Set(prev);
					for (const rid of range) next.add(rid);
					return next;
				});
				showHint("ctrl-shift");
			} else {
				// Shift: replace with range
				setSelected(new Set(range));
				showHint("shift");
			}
			return;
		}

		if (hasCtrl) {
			// Ctrl: toggle single
			setSelected((prev) => {
				const next = new Set(prev);
				if (next.has(id)) next.delete(id);
				else next.add(id);
				return next;
			});
			showHint("ctrl");
		} else {
			// Plain click: toggle
			setSelected((prev) => {
				const next = new Set(prev);
				if (next.has(id)) next.delete(id);
				else next.add(id);
				return next;
			});
			showHint("click");
		}
		lastClicked.current = id;
	}, [showHint]);

	const selectAll = useCallback(() => {
		if (selected.size === ENTRIES.length) {
			setSelected(new Set());
		} else {
			setSelected(new Set(ENTRIES.map((e) => e.id)));
		}
	}, [selected.size]);

	const allSelected = selected.size === ENTRIES.length;
	const someSelected = selected.size > 0 && selected.size < ENTRIES.length;

	return (
		<div className="not-prose flex flex-col gap-3 rounded-lg border bg-muted/30 p-4 sm:p-5">
			{/* Table */}
			<div className="rounded-md border bg-background overflow-hidden">
				{/* Header */}
				<div className="flex items-center gap-3 px-3 py-2 bg-muted/50 border-b text-xs font-medium text-muted-foreground select-none">
					<button
						onClick={selectAll}
						className={cn(
							"h-4 w-4 rounded-sm border border-primary flex items-center justify-center shrink-0 transition-colors",
							allSelected && "bg-primary text-primary-foreground",
							someSelected && "bg-primary text-primary-foreground",
						)}
					>
						{allSelected && (
							<svg className="h-3 w-3" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
						)}
						{someSelected && !allSelected && (
							<svg className="h-3 w-3" viewBox="0 0 12 12" fill="none"><path d="M3 6H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
						)}
					</button>
					{selected.size > 0 && (
						<span className="text-primary font-semibold text-[11px]">{selected.size}</span>
					)}
					<span className="flex-1">Matter</span>
					<span className="w-16 text-right">Time</span>
					<span className="w-12 text-right">Units</span>
				</div>

				{/* Rows */}
				{ENTRIES.map((entry) => {
					const isSelected = selected.has(entry.id);
					return (
						<div
							key={entry.id}
							onClick={(e) => handleClick(entry.id, e)}
							className={cn(
								"flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 cursor-pointer transition-colors select-none",
								isSelected
									? "bg-primary/10"
									: "hover:bg-muted/50",
							)}
						>
							<div
								className={cn(
									"h-4 w-4 rounded-sm border shrink-0 flex items-center justify-center transition-colors",
									isSelected
										? "bg-primary border-primary text-primary-foreground"
										: "border-muted-foreground/30",
								)}
							>
								{isSelected && (
									<svg className="h-3 w-3" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
								)}
							</div>
							<span className="flex-1 text-sm truncate">{entry.matter}</span>
							<span className="w-16 text-right text-xs text-muted-foreground">{entry.time}</span>
							<span className="w-12 text-right text-xs font-medium tabular-nums">{entry.hours}</span>
						</div>
					);
				})}
			</div>

			{/* Hint */}
			<div className="h-5 flex items-center justify-center">
				{hint ? (
					<span className="text-[11px] text-primary font-medium animate-in fade-in-0 duration-200">
						{HINTS[hint]}
					</span>
				) : (
					<span className="text-[11px] text-muted-foreground">
						Try clicking, Ctrl+clicking, and Shift+clicking the rows above
					</span>
				)}
			</div>
		</div>
	);
}
